import QRCode from "qrcode";

/** What goes on the emergency kit sheet. */
export interface EmergencyKitInput {
  readonly email: string;
  /** Where the user signs in: the web address, or the server address in the desktop app. */
  readonly appUrl: string;
  /** Recovery key in its text form ("ABCD-EFGH-…"). */
  readonly recoveryKey: string;
  /** Already formatted for display, e.g. "14.10.2026, 09:12". */
  readonly createdAt: string;
}

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 56;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

/** WinAnsiEncoding code points that differ from Latin-1. */
const WIN_ANSI: Readonly<Record<string, number>> = {
  "€": 0x80,
  "‚": 0x82,
  "„": 0x84,
  "…": 0x85,
  "‘": 0x91,
  "’": 0x92,
  "“": 0x93,
  "”": 0x94,
  "•": 0x95,
  "–": 0x96,
  "—": 0x97
};

function winAnsiByte(ch: string): number {
  const mapped = WIN_ANSI[ch];
  if (mapped !== undefined) {
    return mapped;
  }
  const code = ch.codePointAt(0) ?? 0x3f;
  return code < 0x80 || (code >= 0xa0 && code <= 0xff) ? code : 0x3f;
}

/** A PDF string literal in WinAnsi bytes, with ( ) \ escaped. Returned as a byte-per-char string. */
function pdfString(text: string): string {
  let out = "(";
  for (const ch of text) {
    const byte = winAnsiByte(ch);
    if (byte === 0x28 || byte === 0x29 || byte === 0x5c) {
      out += "\\";
    }
    out += String.fromCharCode(byte);
  }
  return `${out})`;
}

/** Splits text into lines of at most maxChars, at spaces. Good enough for Helvetica body text. */
export function wrapText(text: string, maxChars: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.split(/\s+/).filter((w: string): boolean => w !== "")) {
    if (line === "") {
      line = word;
    } else if (line.length + 1 + word.length <= maxChars) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line !== "") {
    lines.push(line);
  }
  return lines;
}

function num(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

class Content {
  private readonly ops: string[] = [];

  public text(font: "F1" | "F2" | "F3", size: number, x: number, y: number, text: string, gray: number = 0.1): void {
    this.ops.push(`BT /${font} ${num(size)} Tf ${num(gray)} g ${num(x)} ${num(y)} Td ${pdfString(text)} Tj ET`);
  }

  public rect(x: number, y: number, width: number, height: number, gray: number): void {
    this.ops.push(`${num(gray)} g ${num(x)} ${num(y)} ${num(width)} ${num(height)} re f`);
  }

  public strokeRect(x: number, y: number, width: number, height: number, gray: number, lineWidth: number): void {
    this.ops.push(`${num(lineWidth)} w ${num(gray)} G ${num(x)} ${num(y)} ${num(width)} ${num(height)} re S`);
  }

  public line(x1: number, y1: number, x2: number, y2: number, gray: number, lineWidth: number): void {
    this.ops.push(`${num(lineWidth)} w ${num(gray)} G ${num(x1)} ${num(y1)} m ${num(x2)} ${num(y2)} l S`);
  }

  public toString(): string {
    return this.ops.join("\n");
  }
}

/** Draws the QR code of text with its top-left corner at (x, top), size × size points, quiet zone included. */
function drawQr(content: Content, text: string, x: number, top: number, size: number): void {
  const modules = QRCode.create(text, { errorCorrectionLevel: "M" }).modules;
  const quiet = 4;
  const cell = size / (modules.size + 2 * quiet);
  content.rect(x, top - size, size, size, 1);
  for (let row = 0; row < modules.size; row += 1) {
    let col = 0;
    while (col < modules.size) {
      if (modules.get(row, col) === 0) {
        col += 1;
        continue;
      }
      // One rectangle per horizontal run keeps the file small and the edges seamless.
      const start = col;
      while (col < modules.size && modules.get(row, col) !== 0) {
        col += 1;
      }
      content.rect(x + (quiet + start) * cell, top - (quiet + row + 1) * cell, (col - start) * cell, cell, 0);
    }
  }
}

function keyLines(recoveryKey: string): [string, string] {
  const groups = recoveryKey.split("-");
  const half = Math.ceil(groups.length / 2);
  return [groups.slice(0, half).join("-"), groups.slice(half).join("-")];
}

function pageContent(input: EmergencyKitInput): string {
  const c = new Content();
  let y = PAGE_HEIGHT - MARGIN;

  c.rect(0, PAGE_HEIGHT - 10, PAGE_WIDTH, 10, 0.18);
  c.text("F2", 9, MARGIN, y, "PASSWORDFLOW", 0.35);
  y -= 30;
  c.text("F2", 24, MARGIN, y, "Notfall-Kit");
  y -= 20;
  c.text("F1", 11, MARGIN, y, "Wiederherstellung deines Master-Passworts", 0.35);
  y -= 34;

  const rows: ReadonlyArray<[string, string]> = [
    ["Konto", input.email],
    ["Anmeldung", input.appUrl],
    ["Erstellt", input.createdAt]
  ];
  for (const [label, value] of rows) {
    c.text("F1", 9, MARGIN, y, label, 0.45);
    c.text("F2", 11, MARGIN + 80, y, value);
    y -= 18;
  }
  y -= 14;

  const boxTop = y;
  const boxHeight = 176;
  const qrSize = 150;
  c.strokeRect(MARGIN, boxTop - boxHeight, CONTENT_WIDTH, boxHeight, 0.2, 1.2);
  c.text("F2", 10, MARGIN + 18, boxTop - 28, "Wiederherstellungsschlüssel", 0.35);
  const [first, second] = keyLines(input.recoveryKey);
  c.text("F3", 14, MARGIN + 18, boxTop - 62, first);
  c.text("F3", 14, MARGIN + 18, boxTop - 86, second);
  const hint = wrapText("Groß- und Kleinschreibung und Bindestriche spielen keine Rolle. Der QR-Code enthält denselben Schlüssel.", 52);
  let hintY = boxTop - 120;
  for (const line of hint) {
    c.text("F1", 9, MARGIN + 18, hintY, line, 0.45);
    hintY -= 13;
  }
  drawQr(c, input.recoveryKey, MARGIN + CONTENT_WIDTH - qrSize - 13, boxTop - 13, qrSize);
  y = boxTop - boxHeight - 36;

  const sections: ReadonlyArray<[string, string]> = [
    [
      "So stellst du den Zugang wieder her",
      "Öffne PasswordFlow und wähle bei der Anmeldung „Master-Passwort vergessen?“. Gib deine E-Mail und den Schlüssel ein oder lies den QR-Code ein. Ist die Zwei-Faktor-Anmeldung eingeschaltet, bestätigst du danach wie gewohnt mit deinem zweiten Faktor. Dann legst du ein neues Master-Passwort fest und bekommst ein neues Notfall-Kit."
    ],
    [
      "Bewahre dieses Blatt sicher auf",
      "Am besten ausgedruckt an einem verschlossenen Ort, getrennt von deinen Geräten. Wer diesen Schlüssel und deinen zweiten Faktor hat, kann deinen Tresor öffnen. Gib ihn nie weiter – auch nicht an deinen Administrator."
    ],
    [
      "Wann dieses Kit ungültig wird",
      "Sobald du ein neues Notfall-Kit erstellst oder dieses Kit zur Wiederherstellung benutzt. Eine normale Änderung des Master-Passworts lässt es gültig."
    ]
  ];
  for (const [heading, body] of sections) {
    c.text("F2", 11, MARGIN, y, heading);
    y -= 17;
    for (const line of wrapText(body, 92)) {
      c.text("F1", 10, MARGIN, y, line, 0.2);
      y -= 14.5;
    }
    y -= 14;
  }

  c.line(MARGIN, MARGIN + 18, PAGE_WIDTH - MARGIN, MARGIN + 18, 0.75, 0.6);
  c.text("F1", 8.5, MARGIN, MARGIN, "Erstellt in deinem Browser. Der Server kennt diesen Schlüssel nicht und kann ihn nicht ersetzen.", 0.45);
  return c.toString();
}

/** Bytes of a string whose chars are all below 256 (PDF syntax plus WinAnsi text). */
function latin1Bytes(text: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) {
    out[i] = text.charCodeAt(i) & 0xff;
  }
  return out;
}

/** A one-page A4 PDF: account, recovery key as text and QR code, and how to use it. No external fonts or libraries. */
export function buildEmergencyKitPdf(input: EmergencyKitInput): Uint8Array<ArrayBuffer> {
  const content = pageContent(input);
  const font = (base: string): string => `<< /Type /Font /Subtype /Type1 /BaseFont /${base} /Encoding /WinAnsiEncoding >>`;
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 4 0 R /F2 5 0 R /F3 6 0 R >> >> /Contents 7 0 R >>`,
    font("Helvetica"),
    font("Helvetica-Bold"),
    font("Courier-Bold"),
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    `<< /Title ${pdfString("PasswordFlow Notfall-Kit")} /Producer (PasswordFlow) >>`
  ];
  let body = "%PDF-1.4\n%\xe2\xe3\xcf\xd3\n";
  const offsets: number[] = [];
  objects.forEach((object: string, index: number): void => {
    offsets.push(body.length);
    body += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefAt = body.length;
  body += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) {
    body += `${String(offset).padStart(10, "0")} 00000 n \n`;
  }
  body += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info ${objects.length} 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`;
  return latin1Bytes(body);
}

export function emergencyKitFileName(email: string): string {
  const local = email.split("@")[0] ?? "konto";
  const safe = local.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 40);
  return `PasswordFlow-Notfall-Kit-${safe === "" ? "konto" : safe}.pdf`;
}
